import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrandLogo } from '../components/BrandLogo';
import {
  ArrowLeft, ArrowRight, Download, Loader2, Copy, Check,
  Video, Building2, Users, Shield, Mail, QrCode, Globe,
  FileText, MessageCircle, Sparkles, Eye, Camera, Award,
  Mic, Languages, Calendar, CreditCard, Gift, ChevronRight,
  Share2, Link2, BookOpen
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_media-stage-25/artifacts/qiil6l84_brand%20stage%20logo.png";

const pdfStyles = `
  .pdf-render-mode {
    background: #ffffff !important;
    color: #1a1a1a !important;
    padding: 24px !important;
  }
  .pdf-render-mode * {
    color: #1a1a1a !important;
    border-color: #d4d4d8 !important;
  }
  .pdf-render-mode .pdf-brand-header {
    display: flex !important;
  }
  .pdf-render-mode h1, .pdf-render-mode h2, .pdf-render-mode h3 {
    color: #18181b !important;
  }
  .pdf-render-mode .pdf-accent {
    color: #C41E3A !important;
  }
  .pdf-render-mode .pdf-section {
    background: #f4f4f5 !important;
    border-color: #d4d4d8 !important;
  }
  .pdf-render-mode .pdf-card {
    background: #f9fafb !important;
    border: 1px solid #d4d4d8 !important;
  }
  .pdf-render-mode .pdf-green-section {
    background: #ecfdf5 !important;
    border-color: #10b981 !important;
  }
  .pdf-render-mode .pdf-green-section * {
    color: #065f46 !important;
  }
  .pdf-render-mode .pdf-red-section {
    background: #fef2f2 !important;
    border-color: #C41E3A !important;
  }
  .pdf-render-mode .pdf-step-circle {
    background: #fee2e2 !important;
    border-color: #C41E3A !important;
  }
  .pdf-render-mode .pdf-step-circle span {
    color: #C41E3A !important;
  }
  .pdf-render-mode .pdf-hide {
    display: none !important;
  }
  .pdf-render-mode .pdf-footer {
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #d4d4d8;
    text-align: center;
    font-size: 11px;
    color: #71717a !important;
  }
  .pdf-render-mode .pdf-footer * {
    color: #71717a !important;
  }
`;

export default function BrandStageGuidePage() {
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const contentRef = useRef(null);

  const guideUrl = `${window.location.origin}/guide`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(guideUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(`Check out BrandStage — video-first profiles for business leaders: ${guideUrl}`)}`, '_blank');
  };

  const handleShareEmail = () => {
    window.open(`mailto:?subject=${encodeURIComponent('BrandStage — Video-First Business Profiles')}&body=${encodeURIComponent(`Hi,\n\nI thought you might find this interesting — BrandStage helps businesses create professional, video-first digital profiles.\n\nHere's a quick overview: ${guideUrl}\n\nBest regards`)}`);
  };

  const downloadPdf = async () => {
    if (!contentRef.current || generating) return;
    setGenerating(true);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const el = contentRef.current;
      el.classList.add('pdf-render-mode');
      await html2pdf().set({
        margin: [12, 14, 12, 14],
        filename: 'BrandStage_Guide.pdf',
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
      }).from(el).save();
      el.classList.remove('pdf-render-mode');
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      <style>{pdfStyles}</style>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/90 backdrop-blur-md border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors" data-testid="guide-back-btn">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Home</span>
          </button>
          <BrandLogo className="h-10" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 md:px-8 pt-24 pb-16">

        {/* Action bar: Share + Download */}
        <div className="flex flex-wrap items-center justify-end gap-2 mb-6">
          <button
            onClick={handleCopyLink}
            className="flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-white px-3 py-2 rounded-lg text-sm transition-colors border border-zinc-700"
            data-testid="guide-copy-link-btn"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Link2 className="w-3.5 h-3.5" />}
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
          <button
            onClick={handleShareWhatsApp}
            className="flex items-center gap-1.5 bg-[#25D366]/15 hover:bg-[#25D366]/25 text-[#25D366] px-3 py-2 rounded-lg text-sm transition-colors border border-[#25D366]/30"
            data-testid="guide-share-whatsapp-btn"
          >
            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
          </button>
          <button
            onClick={handleShareEmail}
            className="flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-white px-3 py-2 rounded-lg text-sm transition-colors border border-zinc-700"
            data-testid="guide-share-email-btn"
          >
            <Mail className="w-3.5 h-3.5" /> Email
          </button>
          <button
            onClick={downloadPdf}
            disabled={generating}
            className="flex items-center gap-1.5 bg-[#C41E3A] hover:bg-[#A01830] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            data-testid="guide-download-pdf-btn"
          >
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            {generating ? 'Generating...' : 'Download PDF'}
          </button>
        </div>

        {/* ── PDF content area ── */}
        <div ref={contentRef}>

          {/* PDF-only brand header */}
          <div className="pdf-brand-header hidden items-center gap-3 mb-6 pb-4" style={{ borderBottom: '2px solid #C41E3A' }}>
            <img src={LOGO_URL} alt="BrandStage" style={{ height: '36px' }} crossOrigin="anonymous" />
            <div style={{ marginLeft: 'auto', fontSize: '11px', color: '#71717a', textAlign: 'right' }}>
              <span>brandstage-media.com</span><br />
              <span>operations@brandstage-media.com</span>
            </div>
          </div>

          {/* ── 1. HERO: What is BrandStage ── */}
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-[#C41E3A]/10 text-[#C41E3A] px-4 py-1.5 rounded-full text-sm font-medium mb-4 pdf-accent">
              <BookOpen className="w-4 h-4" />
              Quick Guide
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" data-testid="guide-title">
              BrandStage — Your Business, On Camera
            </h1>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto leading-relaxed">
              BrandStage helps businesses and leaders create professional, video-first digital profiles
              that can be shared privately with prospects, partners, and investors — all through a single link.
            </p>
          </div>

          {/* Why BrandStage */}
          <div className="border border-[#C41E3A]/20 rounded-2xl bg-zinc-900/30 p-6 md:p-10 mb-10 pdf-red-section">
            <h2 className="text-xl font-bold text-white mb-4">Why BrandStage?</h2>
            <p className="text-zinc-300 leading-relaxed mb-6">
              In a world where AI is reshaping how businesses are discovered, video profiles give you an edge that text alone cannot.
              BrandStage combines AI-powered content creation with video hosting to produce a polished, shareable
              digital identity for your company or your leadership team.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-zinc-800/50 rounded-lg p-4 text-center pdf-card">
                <Shield className="w-6 h-6 text-[#C41E3A] mx-auto mb-2" />
                <p className="text-white font-semibold text-sm mb-1">Private by Design</p>
                <p className="text-zinc-400 text-xs">Only people you share the link with can see your profile. Not indexed by search engines.</p>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-4 text-center pdf-card">
                <Video className="w-6 h-6 text-[#C41E3A] mx-auto mb-2" />
                <p className="text-white font-semibold text-sm mb-1">Video-First</p>
                <p className="text-zinc-400 text-xs">Host branded videos, leadership interviews, and product showcases on your profile.</p>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-4 text-center pdf-card">
                <Sparkles className="w-6 h-6 text-[#C41E3A] mx-auto mb-2" />
                <p className="text-white font-semibold text-sm mb-1">AI-Powered</p>
                <p className="text-zinc-400 text-xs">AI helps generate your company story, profile sections, and professional content in minutes.</p>
              </div>
            </div>
          </div>

          {/* ── 2. KEY FEATURES ── */}
          <div className="mb-10">
            <h2 className="text-xl font-bold text-white mb-6">Key Features</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { icon: <Building2 className="w-5 h-5 text-[#C41E3A]" />, title: 'Company Profile', desc: 'A professional digital profile for your business — who you are, what you do, your presence, what you seek, and why people should work with you.', included: 'basic' },
                { icon: <Award className="w-5 h-5 text-[#C41E3A]" />, title: 'Leadership Profile', desc: 'A personal brand page for founders, CXOs, and key leaders — their story, career journey, and professional identity.', included: 'basic' },
                { icon: <Video className="w-5 h-5 text-[#C41E3A]" />, title: 'Video Hosting', desc: 'Upload or record branded videos. BrandStage auto-brands them with your logo, intro, and outro.', included: 'basic' },
                { icon: <Mail className="w-5 h-5 text-[#C41E3A]" />, title: 'Email Signature', desc: 'A professional HTML email signature with your profile link, logo, and social handles — ready to paste into Gmail or Outlook.', included: 'basic' },
                { icon: <QrCode className="w-5 h-5 text-[#C41E3A]" />, title: 'QR Code', desc: 'A branded QR code that links directly to your profile — perfect for business cards, trade shows, and printed material.', included: 'basic' },
                { icon: <Users className="w-5 h-5 text-[#C41E3A]" />, title: 'Video Testimonials', desc: 'Collect 45-second video testimonials from clients and partners. They record via a simple link — no login needed.', included: 'pro' },
                { icon: <Languages className="w-5 h-5 text-[#C41E3A]" />, title: 'Multi-Language', desc: 'Your profile automatically translated into multiple languages so international contacts can read it in their language.', included: 'pro' },
                { icon: <Camera className="w-5 h-5 text-[#C41E3A]" />, title: 'Event Galleries', desc: 'Photo galleries and video highlights from trade shows, conferences, and key business moments.', included: 'pro' },
                { icon: <Calendar className="w-5 h-5 text-[#C41E3A]" />, title: 'Event Invitations', desc: 'Send branded event invitations to your contacts directly through BrandStage.', included: 'pro' },
                { icon: <Globe className="w-5 h-5 text-[#C41E3A]" />, title: 'BrandStage Connect', desc: 'Discover and respond to business opportunities posted by other verified BrandStage members. Coming soon — launching when we reach 50+ active profiles.', included: 'soon' },
              ].map((f, i) => (
                <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 flex gap-4 pdf-card">
                  <div className="shrink-0 mt-0.5">{f.icon}</div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-white font-semibold text-sm">{f.title}</h3>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${f.included === 'basic' ? 'bg-emerald-500/20 text-emerald-400' : f.included === 'soon' ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-500/20 text-amber-400'}`}>
                        {f.included === 'basic' ? 'Included' : f.included === 'soon' ? 'Coming Soon' : 'Pro Add-on'}
                      </span>
                    </div>
                    <p className="text-zinc-400 text-xs leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── 3. PRICING OVERVIEW ── */}
          <div className="border border-zinc-800 rounded-2xl bg-zinc-900/30 p-6 md:p-8 mb-10 pdf-section">
            <h2 className="text-xl font-bold text-white mb-2">Pricing</h2>
            <p className="text-zinc-400 text-sm mb-6">
              Creating your profile is free. Keeping it live and visible requires a subscription.
            </p>

            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="bg-zinc-800/50 rounded-xl p-5 border border-zinc-700 pdf-card">
                <p className="text-zinc-400 text-xs uppercase tracking-wider mb-2">Basic · 6 Months</p>
                <p className="text-white font-semibold mb-3">What's included:</p>
                <ul className="space-y-1.5 text-xs text-zinc-300">
                  <li className="flex items-start gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" /> AI-generated company profile</li>
                  <li className="flex items-start gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" /> Video hosting</li>
                  <li className="flex items-start gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" /> Email signature</li>
                  <li className="flex items-start gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" /> QR code</li>
                  <li className="flex items-start gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" /> Private, link-only profile</li>
                </ul>
              </div>
              <div className="bg-zinc-800/50 rounded-xl p-5 border border-zinc-700 pdf-card">
                <p className="text-zinc-400 text-xs uppercase tracking-wider mb-2">Basic · 1 Year</p>
                <p className="text-white font-semibold mb-3">Same features, better value</p>
                <p className="text-zinc-300 text-xs leading-relaxed">
                  All the features of the 6-month plan at a lower effective monthly cost. Ideal for businesses committed to their digital presence.
                </p>
              </div>
              <div className="bg-zinc-800/50 rounded-xl p-5 border border-[#C41E3A]/30 relative overflow-hidden pdf-card">
                <div className="absolute top-0 right-0 bg-[#C41E3A] text-white text-[10px] font-bold px-2.5 py-0.5 rounded-bl-lg">BEST VALUE</div>
                <p className="text-zinc-400 text-xs uppercase tracking-wider mb-2">All-Access · 1 Year</p>
                <p className="text-white font-semibold mb-3">Everything included</p>
                <p className="text-zinc-300 text-xs leading-relaxed">
                  Basic features plus all Pro add-ons: testimonials, multi-language profiles, event galleries, invitations, and more. One plan, no limits.
                </p>
              </div>
            </div>

            <div className="bg-zinc-800/30 rounded-lg p-4 text-center text-sm pdf-card">
              <p className="text-zinc-300">
                Need specific Pro features without the full All-Access plan? You can add individual features to any Basic subscription.
              </p>
              <p className="text-zinc-500 text-xs mt-2">Contact us for a personalised quote — pricing depends on your region and requirements.</p>
            </div>
          </div>

          {/* ── 4. HOW TO GET STARTED ── */}
          <div className="border border-[#C41E3A]/20 rounded-2xl bg-zinc-900/30 p-6 md:p-8 mb-10 pdf-section">
            <h2 className="text-xl font-bold text-white mb-6">How to Get Started</h2>
            <div className="space-y-6">
              {[
                { num: '1', title: 'Create Your Account', desc: 'Sign up at brandstage-media.com with your email. It takes less than a minute. No credit card required.' },
                { num: '2', title: 'Build Your Profile', desc: 'Follow the guided 5-step process. Enter your company details, and our AI generates a professional profile for you. Review, edit, and customise it to your liking.' },
                { num: '3', title: 'Add Videos', desc: 'Upload existing videos or record new ones directly in the browser. BrandStage auto-brands them with your logo and identity.' },
                { num: '4', title: 'Subscribe & Go Live', desc: 'Choose a plan that fits your needs. Once subscribed, your profile goes live on a private link that you control and share.' },
                { num: '5', title: 'Share Your Link', desc: 'Send your profile link to prospects, investors, partners, and clients via email, WhatsApp, or your email signature. Only people with the link can see your profile.' },
              ].map(s => (
                <div key={s.num} className="flex gap-4">
                  <div className="pdf-step-circle w-9 h-9 rounded-full bg-[#C41E3A]/10 border border-[#C41E3A]/30 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-[#C41E3A] font-bold text-sm pdf-accent">{s.num}</span>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">{s.title}</h3>
                    <p className="text-zinc-400 text-sm leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── 5. REFERRAL PROGRAMME ── */}
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-6 mb-10 pdf-green-section">
            <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
              <Gift className="w-5 h-5 text-emerald-400" />
              Refer & Earn
            </h2>
            <p className="text-zinc-300 text-sm leading-relaxed mb-4">
              Know someone who could benefit from BrandStage? Share your unique referral link from your Dashboard.
              When they subscribe, you earn credits towards your own subscription — and they receive a welcome discount on theirs.
            </p>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="bg-zinc-900/50 rounded-lg p-3 text-center pdf-card">
                <p className="text-lg font-bold text-white">Credits</p>
                <p className="text-zinc-500 text-[10px]">6-month referral</p>
              </div>
              <div className="bg-zinc-900/50 rounded-lg p-3 text-center pdf-card">
                <p className="text-lg font-bold text-white">Higher Credits</p>
                <p className="text-zinc-500 text-[10px]">1-year referral</p>
              </div>
              <div className="bg-zinc-900/50 rounded-lg p-3 text-center pdf-card">
                <p className="text-lg font-bold text-[#C41E3A] pdf-accent">Best Rewards</p>
                <p className="text-zinc-500 text-[10px]">All-Access referral</p>
              </div>
            </div>
            <p className="text-zinc-500 text-xs">
              Your referral also gets a <strong className="text-zinc-300">welcome discount</strong> on their first subscription. Credits auto-apply at checkout. Contact us for reward details.
            </p>
          </div>

          {/* ── 6. USE CASES ── */}
          <div className="mb-10">
            <h2 className="text-xl font-bold text-white mb-6">Who is BrandStage for?</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { title: 'Exporters & Manufacturers', desc: 'Share your company story, capabilities, and factory tours with international buyers before the first meeting.' },
                { title: 'Trade Show Exhibitors', desc: 'Replace paper brochures with a scannable QR code that shows your video profile, products, and contact details.' },
                { title: 'Consultants & Advisors', desc: 'Build a personal leadership profile to share with potential clients — testimonials, expertise, and thought leadership.' },
                { title: 'Startups Seeking Investment', desc: 'Send investors a polished company profile with vision, team introductions, and product demos — all in one link.' },
              ].map((uc, i) => (
                <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 pdf-card">
                  <h3 className="text-white font-semibold text-sm mb-2">{uc.title}</h3>
                  <p className="text-zinc-400 text-xs leading-relaxed">{uc.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── 7. CONTACT ── */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 text-center pdf-hide">
            <h3 className="text-xl font-bold text-white mb-3">Ready to get started?</h3>
            <p className="text-zinc-400 mb-6 max-w-lg mx-auto">
              Create your free account, or reach out to our team for a personalised walkthrough.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigate('/register')}
                className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-xl font-medium transition-colors flex items-center gap-2"
                data-testid="guide-register-btn"
              >
                Create Free Account <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="mailto:operations@brandstage-media.com?subject=BrandStage Enquiry"
                className="bg-zinc-800 hover:bg-zinc-700 text-white px-6 py-3 rounded-xl font-medium transition-colors border border-zinc-700 flex items-center gap-2"
                data-testid="guide-contact-btn"
              >
                <Mail className="w-4 h-4" /> Email Us
              </a>
              <a
                href="https://wa.me/919940067960?text=Hi%2C%20I%27d%20like%20to%20know%20more%20about%20BrandStage."
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#25D366]/15 hover:bg-[#25D366]/25 text-[#25D366] px-6 py-3 rounded-xl font-medium transition-colors border border-[#25D366]/30 flex items-center gap-2"
                data-testid="guide-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>
            <div className="mt-6 flex items-center justify-center gap-4 text-xs text-zinc-500">
              <a href="/referral-programme" className="hover:text-white transition-colors flex items-center gap-1" data-testid="guide-referral-link">
                <Gift className="w-3 h-3" /> Referral Programme <ChevronRight className="w-3 h-3" />
              </a>
              <a href="/pricing" className="hover:text-white transition-colors flex items-center gap-1" data-testid="guide-pricing-link">
                <CreditCard className="w-3 h-3" /> View Pricing <ChevronRight className="w-3 h-3" />
              </a>
            </div>
          </div>

          {/* PDF-only contact footer */}
          <div className="hidden pdf-footer" style={{ marginTop: '24px' }}>
            <p><strong>Get Started Today</strong></p>
            <p>Visit: <strong style={{ color: '#C41E3A' }}>brandstage-media.com</strong> &nbsp;|&nbsp; Email: <strong>operations@brandstage-media.com</strong> &nbsp;|&nbsp; WhatsApp: <strong>+91 9940067960</strong></p>
            <p style={{ marginTop: '12px' }}>&copy; {new Date().getFullYear()} BrandStage Media. A Continex Initiative. All rights reserved.</p>
          </div>
        </div>
        {/* ── PDF content ends ── */}
      </main>

      <footer className="border-t border-zinc-800 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-zinc-500 text-sm">&copy; {new Date().getFullYear()} BrandStage. Powered by CONTINEX.</p>
        </div>
      </footer>
    </div>
  );
}
